import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide06NavCom.css';

function Slide06NavCom() {
  const prefersReduced = useReducedMotion();
  const reduceMotion = !!prefersReduced;
  const [isVisible, setIsVisible] = useState(false);
  const [panel, setPanel] = useState(0);
  const panelCount = 3;
  const ease: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleDoubleClick = () => {
    setPanel((prev) => (prev + 1) % panelCount);
  };

  const fadeUp = (delay = 0) =>
    reduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 26 },
          animate: isVisible ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.7, delay, ease },
        };

  return (
    <div className="slide slide-navcom" onDoubleClick={handleDoubleClick}>
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      <div className="navcom-bg">
        <div className="navcom-grid" />
        <div className="navcom-vignette" />
        <div className="navcom-noise" />
      </div>

      <div className="navcom-content">
        <motion.div className="navcom-left" {...fadeUp(0.1)}>
          <h1 className="navcom-title">
            NAVIGATION &amp;
            <br />
            COMMUNICATION
          </h1>
          <p className="navcom-lead">
            The company delivers navigation and communication equipment and services:
          </p>
          <ul className="navcom-list">
            <li>Radar and ECDIS systems</li>
            <li>AIS, GPS, and satellite communication</li>
            <li>GMDSS systems for maritime safety</li>
            <li>New building projects including engineering, documentation and commissioning</li>
            <li>Maintenance, repair and annual surveys</li>
          </ul>
          <p className="navcom-footer">
            Avatec Marine helps vessels maintain full compliance with safety and operational standards worldwide.
          </p>
        </motion.div>

        <motion.div className="navcom-right" {...fadeUp(0.25)}>
          <div className={`navcom-panel navcom-panel--ecdis ${panel === 0 ? 'is-active' : ''}`}>
            <div className={`ecdis ${reduceMotion ? 'ecdis--static' : ''}`}>
              <div className="ecdis-screen">
                <div className="ecdis-topbar">
                  <div className="ecdis-topbar-metrics">
                    <span>HDG 069.5</span>
                    <span>STW 11.6 kn</span>
                    <span>COG 069.7</span>
                    <span>SOG 11.6 kn</span>
                  </div>
                  <div className="ecdis-topbar-alerts">
                    <span className="ecdis-alert ecdis-alert--red" />
                    <span className="ecdis-alert ecdis-alert--yellow" />
                    <span className="ecdis-topbar-mode">ECDIS</span>
                  </div>
                </div>
                <div className="ecdis-body">
                  <div className="ecdis-side ecdis-side-left">
                    <div className="ecdis-panel-title">Monitored Route</div>
                    <div className="ecdis-panel-row">
                      <span>To WPT</span>
                      <span className="ecdis-panel-value">1</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>BWW</span>
                      <span className="ecdis-panel-value">069.5</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>XTD</span>
                      <span className="ecdis-panel-value">0.7 NM</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>ETA</span>
                      <span className="ecdis-panel-value">16:25</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>Dist</span>
                      <span className="ecdis-panel-value">1.62 NM</span>
                    </div>
                    <div className="ecdis-panel-divider" />
                    <div className="ecdis-panel-row">
                      <span>Depth</span>
                      <span className="ecdis-panel-value">6.7 m</span>
                    </div>
                    <div className="ecdis-panel-footer">UTC 20:12:45</div>
                  </div>

                  <div className="ecdis-map">
                    <div className="ecdis-map-tags">
                      <span>Large ENC</span>
                      <span>Display Not Real Time</span>
                    </div>
                    <svg viewBox="0 0 560 320" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="ecdisSea" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#7cc3f6" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#4595da" stopOpacity="0.95" />
                        </linearGradient>
                      </defs>
                      <rect x="0" y="0" width="560" height="320" fill="url(#ecdisSea)" />
                      <path className="ecdis-shallow" d="M0 40 L120 20 L200 40 L260 70 L120 120 L0 120 Z" />
                      <path className="ecdis-shallow" d="M380 20 L560 20 L560 140 L480 120 L420 80 Z" />
                      <g className="ecdis-grid">
                        <path d="M20 60 H540" />
                        <path d="M20 120 H540" />
                        <path d="M20 180 H540" />
                        <path d="M20 240 H540" />
                        <path d="M80 20 V300" />
                        <path d="M160 20 V300" />
                        <path d="M240 20 V300" />
                        <path d="M320 20 V300" />
                        <path d="M400 20 V300" />
                        <path d="M480 20 V300" />
                      </g>
                      <path className="ecdis-channel" d="M40 250 L140 220 L220 200 L300 180 L380 150 L520 120" />
                      <path className="ecdis-route-outline" d="M40 250 L140 220 L220 200 L300 180 L380 150 L520 120" />
                      <path className="ecdis-route" d="M40 250 L140 220 L220 200 L300 180 L380 150 L520 120" />
                      <path className="ecdis-route-secondary" d="M60 270 L170 240 L260 220 L340 200 L420 170 L520 150" />
                      <g className="ecdis-buoys">
                        <circle cx="140" cy="220" r="4" />
                        <circle cx="300" cy="180" r="4" />
                        <circle cx="520" cy="120" r="4" />
                        <circle cx="220" cy="200" r="3" />
                        <circle cx="380" cy="150" r="3" />
                      </g>
                      <polygon className="ecdis-ship" points="220,190 228,205 212,205" />
                      <circle className="ecdis-target" cx="255" cy="175" r="6" />
                      <text className="ecdis-map-label" x="90" y="160">San Francisco</text>
                      <text className="ecdis-map-label" x="360" y="210">Main Channel</text>
                    </svg>
                    <div className="ecdis-map-sweep" />
                  </div>

                  <div className="ecdis-side ecdis-side-right">
                    <div className="ecdis-panel-title">System</div>
                    <div className="ecdis-panel-row">
                      <span>Default Safety Contour</span>
                      <span className="ecdis-panel-value">0800E</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>Safety Depth</span>
                      <span className="ecdis-panel-value">07.5 m</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>Last Update</span>
                      <span className="ecdis-panel-value">05-02-2025</span>
                    </div>
                    <div className="ecdis-panel-divider" />
                    <div className="ecdis-panel-row">
                      <span>GPS</span>
                      <span className="ecdis-panel-value ecdis-panel-value--ok">OK</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>Gyro</span>
                      <span className="ecdis-panel-value">Ready</span>
                    </div>
                    <div className="ecdis-panel-row">
                      <span>Depth</span>
                      <span className="ecdis-panel-value">12.6 m</span>
                    </div>
                  </div>
                </div>
                <div className="ecdis-footer">
                  <span>Tasks</span>
                  <span>List</span>
                  <span>Route</span>
                  <span>Settings</span>
                  <span>Events</span>
                </div>
              </div>

              <div className="ecdis-bezel">
                <div className="ecdis-leds">
                  <span className="ecdis-led ecdis-led--green" />
                  <span className="ecdis-led ecdis-led--amber" />
                  <span className="ecdis-led ecdis-led--red" />
                </div>
                <div className="ecdis-controls">
                  <span className="ecdis-knob" />
                  <span className="ecdis-button">Menu</span>
                  <span className="ecdis-button">RNG</span>
                  <span className="ecdis-button">Gain</span>
                </div>
              </div>
            </div>
          </div>

          <div className={`navcom-panel navcom-panel--ais ${panel === 1 ? 'is-active' : ''}`}>
            <div className={`ais ${reduceMotion ? 'ais--static' : ''}`}>
              <div className="ais-screen">
                <div className="ais-screen-header">
                  <div className="ais-badges">
                    <span className="ais-badge">Class A</span>
                    <span className="ais-badge">TRX</span>
                    <span className="ais-badge">HIGH</span>
                  </div>
                  <div className="ais-mmsi">MMSI : 201506050</div>
                </div>
                <div className="ais-screen-body">
                  <div className="ais-radar">
                    <svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="80" cy="80" r="70" className="ais-radar-ring" />
                      <circle cx="80" cy="80" r="46" className="ais-radar-ring" />
                      <circle cx="80" cy="80" r="22" className="ais-radar-ring" />
                      <line x1="80" y1="10" x2="80" y2="150" className="ais-radar-line" />
                      <line x1="10" y1="80" x2="150" y2="80" className="ais-radar-line" />
                      <polygon points="80,78 86,92 74,92" className="ais-radar-ship" />
                      <polygon points="100,60 106,74 94,74" className="ais-radar-ship" />
                      <rect x="124" y="84" width="12" height="12" className="ais-radar-target" />
                      <text x="110" y="148" className="ais-radar-label">12NM</text>
                    </svg>
                  </div>
                  <div className="ais-data">
                    <div className="ais-data-row">
                      <span>TYPE</span>
                      <span className="ais-data-strong">CLASS A</span>
                    </div>
                    <div className="ais-data-row ais-data-row--big">
                      <span>MMSI</span>
                      <span className="ais-data-strong">351308000</span>
                    </div>
                    <div className="ais-data-row">
                      <span>NAME</span>
                      <span className="ais-data-strong">NAME_NO.2</span>
                    </div>
                    <div className="ais-data-row ais-data-row--stack">
                      <span>POSN</span>
                      <span className="ais-data-strong">34°37.1850&apos;N</span>
                      <span className="ais-data-strong">135°24.5100&apos;E</span>
                    </div>
                    <div className="ais-data-grid">
                      <div>
                        <span>SOG</span>
                        <strong>0.0kn</strong>
                      </div>
                      <div>
                        <span>HDG</span>
                        <strong>89°</strong>
                      </div>
                      <div>
                        <span>COG</span>
                        <strong>264°</strong>
                      </div>
                      <div>
                        <span>BRG</span>
                        <strong>127°</strong>
                      </div>
                      <div className="ais-data-wide">
                        <span>RNG</span>
                        <strong>11.7NM</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ais-screen-footer">
                  <span>CURSOR</span>
                  <span>ENT : FUNC</span>
                  <span>RANGE</span>
                  <span>DISP : NEXT</span>
                </div>
              </div>

              <div className="ais-controls">
                <div className="ais-button-grid">
                  <span className="ais-button">BRILL</span>
                  <span className="ais-button">DISP</span>
                  <span className="ais-button">MENU/ESC</span>
                  <span className="ais-button">NAV STATUS</span>
                  <span className="ais-button">ENT/ACK</span>
                  <span className="ais-button ais-button--power" />
                </div>
                <div className="ais-grill" />
                <div className="ais-pad">
                  <span className="ais-pad-arrow ais-pad-up" />
                  <span className="ais-pad-arrow ais-pad-right" />
                  <span className="ais-pad-arrow ais-pad-down" />
                  <span className="ais-pad-arrow ais-pad-left" />
                  <span className="ais-pad-center" />
                </div>
              </div>
            </div>
          </div>

          <div className={`navcom-panel navcom-panel--gps ${panel === 2 ? 'is-active' : ''}`}>
            <div className="gps">
              <div className="gps-screen">
                <div className="gps-topbar">
                  <div className="gps-top-left">
                    <span className="gps-icon gps-icon-check" aria-hidden="true" />
                    <span className="gps-icon gps-icon-wave" aria-hidden="true" />
                  </div>
                  <div className="gps-top-center">
                    <span>17-04-18</span>
                    <span>03:39:01</span>
                    <span>U</span>
                  </div>
                  <div className="gps-top-right">
                    <span>DIM</span>
                    <span className="gps-dim-plus">+</span>
                  </div>
                </div>
                <div className="gps-subbar">
                  <span className="gps-sub-left">W84</span>
                  <span className="gps-sub-right">
                    <span>SAFE</span>
                    <span>10m</span>
                  </span>
                </div>
                <div className="gps-substatus">
                  <span>AUTO</span>
                  <span>No.1</span>
                  <span>GPS</span>
                  <span>2D Fix</span>
                </div>
                <div className="gps-main">
                  <div className="gps-pos-label">POS</div>
                  <div className="gps-pos">36°36.818&apos; N</div>
                  <div className="gps-pos">138°10.663&apos; E</div>
                </div>
                <div className="gps-footer">
                  <div>
                    <span>SOG</span>
                    <strong>17.23</strong>
                    <em>kn</em>
                  </div>
                  <div>
                    <span>COG</span>
                    <strong>25.5°</strong>
                  </div>
                </div>
              </div>
              <div className="gps-buttons">
                <span className="gps-btn gps-btn-menu">MENU</span>
                <span className="gps-btn">NAV</span>
                <span className="gps-btn">FUNC</span>
                <span className="gps-btn">&lt;</span>
                <span className="gps-btn">&gt;</span>
                <span className="gps-btn">DIM</span>
              </div>
              <div className="gps-mark">GPS</div>
              <div className="gps-power" aria-hidden="true" />
            </div>
          </div>

          <div className="navcom-toggle-hint">Double click</div>
        </motion.div>
      </div>
    </div>
  );
}

export default Slide06NavCom;
